import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { CustomProduct, ReferenceProduct } from '../../models';
import { PulseRender, PulseRendered, Template } from '../../models/pulse';
import {
    DesignCategory,
    DesignCategoryProps,
    DesignerProduct,
    DesignItem,
    DesignResult,
    DesignSearchProps,
    DesignUpload,
    FreeFromPvDataResponse,
    JsonDetails,
    PulseOrderPrint,
    PvShape,
    RetrieveImageDataResponse,
    TextSvgRequest,
} from '../../models/pulsePrint';
import { Customisation } from '../../enums/Customisation';

export const customProductApi = createApi({
    reducerPath: 'customProductApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `/rest/custo/`,
    }),
    endpoints: (builder) => ({
        getTemplate: builder.mutation<Template, { code: string }>({
            query: (args) => ({
                url: `pulse/templates/${args.code}`,
                method: 'GET',
            }),
        }),
        createCustomProduct: builder.mutation<CustomProduct, CustomProduct>({
            query: (customProduct) => ({
                url: `custom/create`,
                method: 'POST',
                body: customProduct,
            }),
        }),
        getReferenceProduct: builder.query<
            ReferenceProduct,
            { sku: string | undefined; bu: string | undefined }
        >({
            query: (args) => ({
                url: `products/${args.sku}`,
                params: { bu: args.bu },
                method: 'GET',
            }),
        }),
        getBatch: builder.query<boolean, { sku: string | undefined }>({
            query: (args) => ({
                url: `/products/${args.sku}/batch`,
                method: 'GET',
            }),
        }),
        getCustomizationType: builder.query<
            Customisation[],
            { sku: string | undefined; bu: string | undefined }
        >({
            query: (args) => ({
                url: `customizations`,
                params: { sku: args.sku, bu: args.bu },
                method: 'GET',
            }),
        }),
        getCallbackUrl: builder.query<
            string,
            {
                env: string | undefined;
                bu: string | undefined;
                neutron: boolean | undefined;
            }
        >({
            query: (args) => ({
                url: 'callbackurl',
                params: { ...args },
                method: 'GET',
                responseHandler: (response) => response.text(),
            }),
        }),
        renderTemplate: builder.mutation<
            PulseRendered,
            PulseRender | undefined
        >({
            query: (pulseRender) => ({
                url: `pulse/render`,
                method: 'POST',
                body: pulseRender,
            }),
        }),
        createPulseIdJob: builder.mutation<
            string,
            PulseRender | PulseOrderPrint | undefined
        >({
            query: (pulseRender) => ({
                url: `pulse/create`,
                method: 'POST',
                body: pulseRender,
                responseHandler: (response) => response.text(),
            }),
        }),
        // TODO : changer le mutation en query
        checkBlacklist: builder.mutation<string, { text: string }>({
            query: (args) => ({
                url: 'pulse/checkBlacklist',
                params: { ...args },
                method: 'GET',
                responseHandler: (response) => response.text(),
            }),
        }),
        getFontSvg: builder.query<string, { fontName: string }>({
            query: (args) => ({
                url: `pulse/fonts/svg/${args.fontName}`,
                method: 'GET',
                responseHandler: async (response) => {
                    const svgContent = await response.text();
                    localStorage.setItem(args.fontName, svgContent);
                    return svgContent;
                },
            }),
        }),
        uploadPrintFromUser: builder.mutation<DesignUpload, FormData>({
            query: (args) => ({
                url: 'pulse/designs/uploadPrintFromUser',
                method: 'POST',
                body: args,
            }),
        }),
        uploadQrCode: builder.mutation<DesignUpload, FormData>({
            query: (args) => ({
                url: 'pulse/designs/uploadQrCode',
                method: 'POST',
                body: args,
            }),
        }),
        getFreeFormPvData: builder.mutation<
            RetrieveImageDataResponse,
            DesignUpload
        >({
            query: (args) => ({
                url: 'pulse/designs/freeFormPvData',
                method: 'POST',
                body: args,
            }),
        }),
        getDesignerProductBySku: builder.query<
            DesignerProduct,
            { sku: string }
        >({
            query: (args) => ({
                url: `/pulse/designer/product/${args.sku}`,
                method: 'GET',
            }),
        }),
        getProductBySku: builder.query<DesignerProduct, { sku: string }>({
            query: (args) => ({
                url: `/pulse/product/${args.sku}`,
                method: 'GET',
            }),
        }),
        getDesignCategories: builder.query<DesignCategory[], void>({
            query: () => ({
                url: `pulse/designCategories`,
                method: 'GET',
            }),
        }),
        getDesignsByCategory: builder.query<DesignItem[], DesignCategoryProps>({
            query: (args) => ({
                url: `pulse/designsByCategory`,
                method: 'GET',
                params: { ...args },
            }),
        }),
        getDesignsBySearch: builder.query<DesignItem[], DesignSearchProps>({
            query: (args) => ({
                url: `pulse/designsBySearch`,
                method: 'GET',
                params: { ...args },
            }),
        }),
        submitPrintOrder: builder.mutation<string, PulseOrderPrint>({
            query: (args) => ({
                url: 'pulse/createPrintOrder',
                method: 'POST',
                body: args,
                responseHandler: (response) => response.text(),
            }),
        }),
        editFreeFormPvData: builder.mutation<PvShape, TextSvgRequest>({
            query: (args) => ({
                url: 'pulse/text/editFreeFormPvData',
                method: 'POST',
                body: args,
            }),
        }),
    }),
});

export const {
    useGetBatchQuery,
    useCreateCustomProductMutation,
    useGetReferenceProductQuery,
    useGetCustomizationTypeQuery,
    useGetCallbackUrlQuery,
    useRenderTemplateMutation,
    useCreatePulseIdJobMutation,
    useCheckBlacklistMutation,
    useGetFontSvgQuery,
    useUploadPrintFromUserMutation,
    useUploadQrCodeMutation,
    useGetFreeFormPvDataMutation,
    useGetDesignerProductBySkuQuery,
    useGetProductBySkuQuery,
    useSubmitPrintOrderMutation,
    useGetDesignCategoriesQuery,
    useGetDesignsByCategoryQuery,
    useGetDesignsBySearchQuery,
    useEditFreeFormPvDataMutation,
    useGetTemplateMutation,
} = customProductApi;
